import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { jsx as _jsx } from "react/jsx-runtime";
import { Suspense, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { BottomSheetContainerProvider, OverlayProvider, useStateInRef } from 'seagull';
import { GlobalLoader } from 'seagull/components/OverlayContext/GlobalLoader';
import { MediaQueriesProvider } from 'seagull/MediaQueries';
import { isTestMode } from 'seagull/utils/test-mode';
import useEventTargets from '~/event-reporter/event-targets/useEventTargets';
import { Observable } from '~/infra/Observable/Observable';
import { BillingObservableProvider } from '~/providers/BillingObservableProvider';
import { queryClient } from '~/queryClient';
import { BiFrontendProvider } from './components/BiFrontendProvider/BiFrontendProvider';
import ErrorBoundaryPage from './components/ErrorBoundaryPage';
import { useIoContext, useStore, useStoreActions } from './contexts';
import { EventReporterProvider } from './event-reporter';
import { useManageLocationStack } from './hooks/useManageLocationStack';
import { getIntentBehavior } from './infra/access-control';
import './infra/styles.scss';
import './infra/tailwind.css';
import { IntercomStarter } from './intercom/IntercomStarter';
import Main from './pages/Main/Main';
import AuthProvider from './providers/AuthProvider';
import { InvoiceChangesObservableProvider } from './providers/InvoiceChangesObservableProvider';
import { ModalCloseManagerProvider } from './providers/ModalCloseManager';
import SeagullIntentBehaviorProvider from './providers/SeagullIntentBehaviorProvider';
import { PusherBusinessChannelProvider } from './pusher';
import { selectors } from './store/state-selectors';
function loadAppcues(appcuesAccountId) {
    const appcuesScriptId = 'appcues-bootstrap';
    return new Promise((resolve)=>{
        if (document.getElementById(appcuesScriptId)) {
            resolve();
        } else {
            window.AppcuesSettings = {
                enableURLDetection: true
            };
            window.appcuesAccountId = appcuesAccountId;
            const script = document.createElement('script');
            script.id = appcuesScriptId;
            script.defer = true;
            script.crossOrigin = 'anonymous';
            script.type = 'text/javascript';
            script.src = 'https://cdn.anchor.sh/appcues/appcues-bootstrap.js';
            script.integrity = 'sha384-0WU2TUOJTyy11Iqf6ViiHLuqyhmagqyTyHnyxmMoRnKHAIK/wef8Y/KqAbvIFHaY';
            script.onload = ()=>resolve();
            document.head.appendChild(script);
        }
    });
}
/** Wraps all modals with an error boundary modal. */ const ModalWrapper = (props)=>/*#__PURE__*/ _jsx(ErrorBoundaryPage, _object_spread({
        isModal: true
    }, props));
export function App() {
    const { data } = useStore(selectors.ff.defaultFeatureFlags);
    const location = useLocation();
    const { search } = location;
    const getStateInRef = useStateInRef({
        search
    });
    const { setAuthToken } = useStoreActions();
    const { cookies } = useIoContext();
    useManageLocationStack({
        initialLocations: [
            location
        ]
    });
    const hasDefaultFeatureFlags = !!data;
    useEffect(()=>{
        if (!hasDefaultFeatureFlags) return;
        const { search } = getStateInRef();
        // Override feature-flags from URL.
        const featureFlags = new URLSearchParams(search).get('ff');
        cookies.setCookie('ff', featureFlags || '');
        const authToken = cookies.getAuthToken();
        setAuthToken(authToken);
    }, [
        hasDefaultFeatureFlags
    ]);
    const visitorSyncFrame = useMemo(()=>{
        var _document_getElementById;
        return (_document_getElementById = document.getElementById('visitor-sync-frame')) === null || _document_getElementById === void 0 ? void 0 : _document_getElementById.contentWindow;
    }, []);
    const billingObservable = useMemo(()=>new Observable(), []);
    const invoiceChangesObservable = useMemo(()=>new Observable(), []);
    return /*#__PURE__*/ _jsx(QueryClientProvider, {
        client: queryClient,
        children: /*#__PURE__*/ _jsx(BiFrontendProvider, {
            children: /*#__PURE__*/ _jsx(SeagullIntentBehaviorProvider, {
                getIntentBehavior: getIntentBehavior,
                children: /*#__PURE__*/ _jsx(ErrorBoundaryPage, {
                    children: /*#__PURE__*/ _jsx(Suspense, {
                        fallback: /*#__PURE__*/ _jsx(GlobalLoader, {}),
                        children: /*#__PURE__*/ _jsx(MediaQueriesProvider, {
                            children: /*#__PURE__*/ _jsx(AuthProvider, {
                                loadAppcues: loadAppcues,
                                visitorSyncFrame: visitorSyncFrame,
                                visitorSyncOrigin: window._visitorSyncHostname || '',
                                children: /*#__PURE__*/ _jsx(IntercomStarter, {
                                    children: /*#__PURE__*/ _jsx(PusherBusinessChannelProvider, {
                                        children: /*#__PURE__*/ _jsx(BillingObservableProvider, {
                                            observable: billingObservable,
                                            children: /*#__PURE__*/ _jsx(InvoiceChangesObservableProvider, {
                                                observable: invoiceChangesObservable,
                                                children: /*#__PURE__*/ _jsx(BottomSheetContainerProvider, {
                                                    children: /*#__PURE__*/ _jsx(OverlayProvider, {
                                                        disableEnforceFocus: true,
                                                        ModalWrapper: ModalWrapper,
                                                        isTestMode: isTestMode,
                                                        children: /*#__PURE__*/ _jsx(ModalCloseManagerProvider, {
                                                            children: /*#__PURE__*/ _jsx(Main, {})
                                                        })
                                                    })
                                                })
                                            })
                                        })
                                    })
                                })
                            })
                        })
                    })
                })
            })
        })
    });
}
export function AppWithEventReporter() {
    return /*#__PURE__*/ _jsx(EventReporterProvider, {
        eventTargets: useEventTargets(),
        children: /*#__PURE__*/ _jsx(App, {})
    });
}
